import { Form, Formik, FormikProps } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import FormikButton from '../../../common/formik/FormikButton';
import FormikGrid from '../../../common/formik/FormikGrid';
import { InlineFormControl } from '../../../common/inline/InlineFormControl';
import JiraIssueTypePicker from '../pickers/JiraIssueTypePicker';
import JiraProjectPicker from '../pickers/JiraProjectPicker';
import { useCreateJiraIssue, useFindJiraIssues } from '../JiraConnect.hooks';
import { JiraIssue, JiraIssueCreateResponse } from '../JiraConnect.interface';

const StyledDialogContent = styled(DialogContent)`
  .checkbox {
    padding-top: 0;
    padding-bottom: 0;
  }
`;

interface FormDto {
  projectId?: string;
  issueTypeId?: string;
  linkToEntity?: boolean;
  duplicateTitle?: boolean;
  duplicateDescription?: boolean;
}

export interface JiraIssueCreateDialogProps {
  open: boolean;
  entity: {
    title: string;
    taskId: number;
    description?: string;
  };
  onCancel?: () => void;
  onCreate?: (issue: JiraIssue, shouldLink: boolean | undefined) => void;
}

const JiraIssueCreateDialog: React.FC<JiraIssueCreateDialogProps> = props => {
  const { open, entity, onCancel, onCreate } = props;
  const formikRef = useRef<FormikProps<FormDto> | null>(null);
  const onCreateRef = useRef(onCreate);
  const [form, setForm] = useState<FormDto>();
  const [issue, setIssue] = useState<JiraIssue>();
  const [result, setResult] = useState<JiraIssueCreateResponse>();
  const [createResult, createIssue] = useCreateJiraIssue();
  const [foundResult, findIssues] = useFindJiraIssues();
  const createdIssues = foundResult.data;
  const isLoading = !!(createResult.isLoading || foundResult.isLoading);
  const error = createResult.error || foundResult.error;

  useEffect(() => {
    if (!open) {
      setForm(undefined);
      setIssue(undefined);
      setResult(undefined);
    }
  }, [open]);

  useEffect(() => {
    onCreateRef.current = onCreate;
  }, [onCreate]);

  useEffect(() => {
    formikRef.current?.setSubmitting(isLoading);
  }, [isLoading]);

  useEffect(() => {
    setResult(createResult.data);
  }, [createResult.data]);

  useEffect(() => {
    if (form && result) {
      findIssues({
        title: result.key,
        data: { reconcileIssues: [Number(result.id)] }
      });
    }
  }, [form, result, findIssues]);

  useEffect(() => {
    const issue = createdIssues?.data[0];
    issue && setIssue(issue);
  }, [createdIssues]);

  useEffect(() => {
    if (issue) {
      onCreateRef.current?.(issue, form?.linkToEntity);
    }
  }, [issue, form?.linkToEntity]);

  return (
    <Dialog open={open} onClose={onCancel}>
      <Formik
        innerRef={formikRef}
        initialValues={{}}
        validationSchema={Yup.object({
          projectId: Yup.string().required('Required'),
          issueTypeId: Yup.string().required('Required')
        })}
        onSubmit={values => {
          if (!values.projectId || !values.issueTypeId) {
            return;
          }
          setForm(values);
          setResult(undefined);
          createIssue({
            fields: {
              summary: entity.title,
              description: entity.description,
              project: { id: values.projectId },
              issuetype: { id: values.issueTypeId }
            }
          });
        }}
      >
        {({ values, errors, touched, setFieldValue }) => (
          <Form autoComplete='off'>
            <DialogTitle>Create Jira Issue</DialogTitle>
            <StyledDialogContent>
              <FormikGrid>
                <InlineFormControl>
                  <JiraProjectPicker
                    value={values.projectId}
                    disabled={isLoading}
                    textFieldProps={{
                      helperText: errors.projectId,
                      error: touched.projectId && !!errors.projectId
                    }}
                    onChange={projectId => {
                      setFieldValue('projectId', projectId, true);
                      setFieldValue('issueTypeId', undefined, true);
                    }}
                  />
                </InlineFormControl>
                <InlineFormControl>
                  <JiraIssueTypePicker
                    value={values.issueTypeId}
                    projectId={values.projectId}
                    disabled={isLoading || !values.projectId}
                    textFieldProps={{
                      helperText: errors.issueTypeId,
                      error: touched.issueTypeId && !!errors.issueTypeId
                    }}
                    onChange={issueType => {
                      setFieldValue('issueTypeId', issueType?.id, true);
                    }}
                  />
                </InlineFormControl>
                <InlineFormControl className='checkbox'>
                  <FormControlLabel
                    label='Link to Current QMS Entity'
                    control={
                      <Checkbox
                        color='primary'
                        disabled={isLoading}
                        checked={values.linkToEntity || false}
                        onChange={(_, checked) => {
                          setFieldValue('linkToEntity', checked, true);
                        }}
                      />
                    }
                  />
                </InlineFormControl>
                <InlineFormControl className='checkbox'>
                  <FormControlLabel
                    label='Duplicate Title Text'
                    control={
                      <Checkbox
                        color='primary'
                        disabled={isLoading}
                        checked={values.duplicateTitle || false}
                        onChange={(_, checked) => {
                          setFieldValue('duplicateTitle', checked, true);
                        }}
                      />
                    }
                  />
                </InlineFormControl>
                <InlineFormControl className='checkbox'>
                  <FormControlLabel
                    label='Duplicate Description Text'
                    control={
                      <Checkbox
                        color='primary'
                        disabled={isLoading}
                        checked={values.duplicateDescription || false}
                        onChange={(_, checked) => {
                          setFieldValue('duplicateDescription', checked, true);
                        }}
                      />
                    }
                  />
                </InlineFormControl>
              </FormikGrid>
              {error && <Alert severity='error'>{error.message}</Alert>}
            </StyledDialogContent>
            <DialogActions>
              <Button onClick={onCancel}>Cancel</Button>
              <FormikButton>Create</FormikButton>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default JiraIssueCreateDialog;
